import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { ArrowRight, Loader2, Mail, CheckCircle2, AlertCircle, KeyRound } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const submit = async (e) => {
    e.preventDefault();
    if (!emailValid) return;
    setLoading(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen-safe grid place-items-center cream-gradient p-4" data-testid="forgot-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-teal-700 mb-6" data-testid="back-to-login">
          <ArrowRight className="h-4 w-4" />
          العودة لتسجيل الدخول
        </Link>

        <div className="bg-white rounded-2xl border border-cream-200 card-shadow p-5">
          <Link to="/" className="mb-6 block"><Logo variant="navy" size={96} /></Link>

          {sent ? (
            <div className="space-y-4" data-testid="forgot-sent">
              <div className="h-14 w-14 rounded-full bg-emerald-50 grid place-items-center">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-heading font-black text-slate-900">تحقّق من بريدك</h1>
              <p className="text-slate-600 text-sm leading-relaxed">
                إذا كان البريد <span className="font-semibold text-slate-900" dir="ltr">{email}</span> مسجّلاً لدينا، فقد أرسلنا رابط إعادة التعيين إليه. الرابط صالح لمدة ساعة واحدة.
              </p>
              <div className="pt-2">
                <Link to="/login" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 w-full">
                  العودة لتسجيل الدخول
                </Link>
              </div>
              <button onClick={() => { setSent(false); setEmail(""); }} className="text-xs text-slate-500 hover:text-teal-700" data-testid="forgot-retry">
                استخدم بريداً آخر
              </button>
            </div>
          ) : (
            <>
              <div className="h-14 w-14 rounded-full bg-teal-50 grid place-items-center mb-4">
                <KeyRound className="h-7 w-7 text-teal-700" />
              </div>
              <h1 className="text-2xl font-heading font-black text-slate-900">نسيت كلمة المرور؟</h1>
              <p className="mt-2 text-slate-600 text-sm">أدخل بريدك المسجّل وسنُرسل لك رابطاً لإنشاء كلمة مرور جديدة.</p>

              <form onSubmit={submit} noValidate className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">البريد الإلكتروني</label>
                  <div className="relative">
                    <Mail className="h-4 w-4 text-slate-400 absolute inset-y-0 start-3 my-auto" />
                    <input
                      type="email" required autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-lg border border-slate-300 bg-white ps-10 pe-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                      data-testid="forgot-email-input"
                      dir="ltr"
                    />
                  </div>
                </div>

                {error && (
                  <div role="alert" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100 flex items-start gap-2" data-testid="forgot-error">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !emailValid}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60"
                  data-testid="forgot-submit"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                  إرسال رابط الاستعادة
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
